import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import RefreshIcon from "@material-ui/icons/Refresh";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  fieldRow: {
    marginBottom: theme.spacing(2),
  },
  keyPreview: {
    display: "inline-block",
    fontFamily: "monospace",
    background: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: "0.9rem",
    marginTop: theme.spacing(0.5),
  },
  divider: {
    margin: `${theme.spacing(3)}px 0`,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(2),
  },
  logoImage: {
    height: 40,
    maxWidth: 120,
    objectFit: "contain",
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    padding: 4,
  },
  logoMeta: {
    flex: 1,
  },
  logoKey: {
    fontFamily: "monospace",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  logoBrand: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: 2,
  },
  logoUrl: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    wordBreak: "break-all",
    marginTop: 2,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  successMsg: {
    color: "#155724",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  errorMsg: {
    color: "#721c24",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
  },
  previewBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
  },
  previewImg: {
    height: 50,
    maxWidth: 150,
    objectFit: "contain",
  },
  orDivider: {
    textAlign: "center",
    margin: `${theme.spacing(2)}px 0`,
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: 0,
      right: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& span": {
      position: "relative",
      background: "#fff",
      padding: "0 12px",
    },
  },
  fileInputLabel: {
    display: "inline-block",
    cursor: "pointer",
  },
  hiddenInput: {
    display: "none",
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
});

function normalizeBrandKey(brand) {
  if (!brand || !brand.trim()) return "";
  return brand.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

class BrandLogoManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logos: [],
      loading: true,
      saving: false,
      error: null,
      successMsg: null,

      // Form fields
      brandInput: "",
      imageUrl: "",
      imageData: null,
      imageDataName: null,
      previewSrc: null,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.fetchLogos();
  }

  fetchLogos = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/brand-logos`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load logos");
      this.setState({ logos: data.logos || [], loading: false });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleBrandInput = (e) => {
    this.setState({ brandInput: e.target.value, successMsg: null });
  };

  handleImageUrl = (e) => {
    const val = e.target.value;
    this.setState({ imageUrl: val, imageData: null, imageDataName: null, previewSrc: val || null, successMsg: null });
  };

  handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      this.setState({
        imageData: ev.target.result,
        imageDataName: file.name,
        imageUrl: "",
        previewSrc: ev.target.result,
        successMsg: null,
      });
    };
    reader.readAsDataURL(file);
  };

  handleSave = async () => {
    const { brandInput, imageUrl, imageData } = this.state;

    if (!brandInput.trim()) {
      this.setState({ error: "Please enter a brand name" });
      return;
    }
    if (!imageUrl && !imageData) {
      this.setState({ error: "Please provide an image URL or upload a file" });
      return;
    }

    this.setState({ saving: true, error: null, successMsg: null });
    try {
      const body = { brand: brandInput };
      if (imageUrl) body.imageUrl = imageUrl;
      if (imageData) body.imageData = imageData;

      const res = await fetch(`${basePath}/api/v1/admin/brand-logos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");

      this.setState({
        saving: false,
        successMsg: `לוגו עבור "${data.logo.brand}" (${data.logo.brandKey}) נשמר בהצלחה!`,
        brandInput: "",
        imageUrl: "",
        imageData: null,
        imageDataName: null,
        previewSrc: null,
      });
      if (this.fileInputRef.current) this.fileInputRef.current.value = "";
      this.fetchLogos();
    } catch (err) {
      this.setState({ error: err.message, saving: false });
    }
  };

  handleDelete = async (brandKey) => {
    if (!window.confirm(`למחוק את הלוגו עבור "${brandKey}"?`)) return;
    try {
      const res = await fetch(`${basePath}/api/v1/admin/brand-logos/${brandKey}`, {
        method: "DELETE",
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to delete");
      this.fetchLogos();
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  renderForm() {
    const { classes } = this.props;
    const { brandInput, imageUrl, imageDataName, previewSrc, saving, successMsg, error } = this.state;
    const previewKey = normalizeBrandKey(brandInput);

    return (
      <Paper className={classes.section} elevation={1}>
        <Typography variant="h6" gutterBottom>
          הוספת / עדכון לוגו מותג
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          הזן את שם המותג (כפי שמופיע במוצר), ואז בחר תמונה — כתובת URL או העלאה מקובץ.
          המפתח חייב להתאים לשם המותג כפי שמגיע מ-Georgia API (ירידה לאותיות קטנות, ללא תווים מיוחדים).
        </Typography>

        <div className={classes.fieldRow}>
          <TextField
            label="שם מותג (כפי שמגיע מה-API, לדוגמה: CST)"
            fullWidth
            variant="outlined"
            size="small"
            value={brandInput}
            onChange={this.handleBrandInput}
          />
          {previewKey && (
            <div>
              <span className={classes.keyPreview}>{previewKey}</span>
              <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
                ← מפתח שישמש בקוד
              </Typography>
            </div>
          )}
        </div>

        <div className={classes.fieldRow}>
          <TextField
            label="כתובת URL של תמונה"
            fullWidth
            variant="outlined"
            size="small"
            value={imageUrl}
            onChange={this.handleImageUrl}
            placeholder="https://example.com/logo.png"
          />
        </div>

        <div className={classes.orDivider}>
          <span>או</span>
        </div>

        <div className={classes.fieldRow}>
          <label className={classes.fileInputLabel}>
            <Button variant="outlined" component="span" startIcon={<ImageIcon />} size="small">
              {imageDataName ? imageDataName : "העלאת קובץ תמונה"}
            </Button>
            <input
              type="file"
              accept="image/*"
              className={classes.hiddenInput}
              ref={this.fileInputRef}
              onChange={this.handleFileChange}
            />
          </label>
        </div>

        {previewSrc && (
          <div className={classes.previewBox}>
            <img
              key={previewSrc}
              src={previewSrc}
              alt="preview"
              className={classes.previewImg}
              onError={(e) => { e.currentTarget.hidden = true; }}
              onLoad={(e) => { e.currentTarget.hidden = false; }}
            />
            <Typography variant="caption" color="textSecondary">
              תצוגה מקדימה
            </Typography>
          </div>
        )}

        {successMsg && <div className={classes.successMsg}>{successMsg}</div>}
        {error && <div className={classes.errorMsg}>{error}</div>}

        <Button
          variant="contained"
          color="primary"
          onClick={this.handleSave}
          disabled={saving}
          className={classes.saveButton}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "שומר..." : "שמור לוגו"}
        </Button>
      </Paper>
    );
  }

  renderLogoList() {
    const { classes } = this.props;
    const { logos, loading } = this.state;

    return (
      <Paper className={classes.section} elevation={1}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <Typography variant="h6">לוגואים מותאמים שמורים</Typography>
          <Tooltip title="רענן רשימה">
            <IconButton size="small" onClick={this.fetchLogos}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : logos.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body2">אין לוגואים מותאמים. הוסף לוגו ראשון למותג.</Typography>
          </div>
        ) : (
          logos.map((logo) => (
            <div key={logo.brandKey} className={classes.logoRow}>
              <img
                src={logo.cdnUrl}
                alt={logo.brandKey}
                className={classes.logoImage}
                onError={(e) => { e.currentTarget.hidden = true; }}
                onLoad={(e) => { e.currentTarget.hidden = false; }}
              />
              <div className={classes.logoMeta}>
                <div className={classes.logoKey}>{logo.brandKey}</div>
                <div className={classes.logoBrand}>{logo.brand}</div>
                <div className={classes.logoUrl}>{logo.cdnUrl}</div>
              </div>
              <Tooltip title="מחק לוגו">
                <IconButton size="small" onClick={() => this.handleDelete(logo.brandKey)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          ))
        )}
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <div className={classes.root}>
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
          ניהול לוגואים עבור מותגים שאינם בספרייה הקבועה (כגון מותגים מ-Georgia API). הלוגואים נשמרים ב-S3 ומוצגים בכרטיסי המוצרים ובדפי המוצר.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {this.renderForm()}
          </Grid>
          <Grid item xs={12} md={6}>
            {this.renderLogoList()}
          </Grid>
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(BrandLogoManager);
